import React from 'react';
import { usePxToVw } from '../../hooks/usePxToVw';

/**
 * Reusable Language Box Component
 * Displays a language option with flag/icon and text
 */
const LanguageBox = ({
  language,
  customization,
  isSelected = false,
  onClick,
  size = 'normal', // 'small', 'normal', 'large'
  customIconUrl = null, // Per-language custom icon
  customIconSize = null, // Per-language custom icon size
  customDisplayName = null, // Per-language custom display name
}) => {
  const { pxToVw } = usePxToVw();
  const c = customization;

  console.log("customIConSize", customIconSize)
  
  // Size multipliers
  const sizeMultiplier = size === 'small' ? 0.6 : size === 'large' ? 1.2 : 1;
  
  // Use custom icon size if provided, otherwise use flagSize
  const iconSize = (customIconUrl && customIconSize ? customIconSize : (c.flagSize || 60)) * sizeMultiplier;
  
  // Convert hex color to rgba with opacity
  const hexToRgba = (hex, opacity) => {
    if (!hex) return 'transparent';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
  };
  
  // Generate box shadow string
  const getBoxShadow = (h, v, blur, spread, color, opacity) => {
    const shadowColor = hexToRgba(color, opacity);
    return `${h}px ${v}px ${blur}px ${spread}px ${shadowColor}`;
  };
  
  const boxStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: pxToVw((c.gap || 10) * sizeMultiplier),
    padding: pxToVw((c.innerPadding || 15) * sizeMultiplier),
    backgroundColor: hexToRgba(c.innerBackgroundColor, isSelected ? Math.min(c.innerOpacity + 10, 100) : c.innerOpacity),
    borderRadius: pxToVw((c.innerBorderRadius || 9) * sizeMultiplier),
    border: `${pxToVw((c.innerBorderWidth || 2) * sizeMultiplier)} solid ${isSelected ? c.innerBorderColor : 'transparent'}`,
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    minWidth: size === 'small' ? pxToVw(100 * sizeMultiplier) : pxToVw(150 * sizeMultiplier),
    boxShadow: getBoxShadow(
      (c.innerShadowH || 0) * sizeMultiplier,
      (c.innerShadowV || 2) * sizeMultiplier,
      (c.innerShadowBlur || 8) * sizeMultiplier,
      (c.innerShadowSpread || 0) * sizeMultiplier,
      c.innerShadowColor || '#000000',
      c.innerShadowOpacity || 15
    ),
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/gh/lipis/flag-icons@6.6.6/css/flag-icons.min.css"
      />
      <div
        style={boxStyle}
        onClick={onClick}
        onMouseEnter={(e) => {
          if (onClick && !isSelected) {
            e.currentTarget.style.backgroundColor = hexToRgba(c.innerBackgroundColor, Math.min(c.innerOpacity + 10, 100));
          }
        }}
        onMouseLeave={(e) => {
          if (onClick && !isSelected) {
            e.currentTarget.style.backgroundColor = hexToRgba(c.innerBackgroundColor, c.innerOpacity);
          }
        }}
      >
        {/* Icon/Flag */}
        {c.enableIcon && (
          customIconUrl ? (
            <img
              src={customIconUrl}
              alt={language.name}
              style={{
                width: pxToVw(iconSize),
                height: pxToVw(iconSize),
                objectFit: 'contain',
                borderRadius: pxToVw(4 * sizeMultiplier),
              }}
            />
          ) : (
            <span 
              className={`fi fi-${language.flag}`}
              style={{ 
                fontSize: pxToVw(iconSize),
                lineHeight: 1,
                borderRadius: pxToVw(4 * sizeMultiplier),
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            />
          )
        )}
        
        {/* Language Name */}
        <span
          style={{
            fontFamily: c.fontFamily !== 'inherit' ? c.fontFamily : undefined,
            fontSize: pxToVw((c.fontSize || 18) * sizeMultiplier),
            fontWeight: c.fontWeight || '700',
            color: c.textColor || '#ffffff',
            whiteSpace: 'nowrap',
          }}
        >
          {customDisplayName || language.name}
        </span>
      </div>
    </>
  );
};

export default LanguageBox;
